import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, ArrowUpDown } from "lucide-react";
import { DocsViewer } from "../components/DocsViewer";
import {
  adminListCompanies, approveCompany, rejectCompany, reopenCompany, pauseCompany, deleteCompany,
  adminListUsers, adminListTemplates, promoteTemplate, adminDeleteTemplate, getUser,
  adminGetWhatsapp, adminSaveWhatsapp,
} from "../lib/api";
import "../studio.css";

const STATUS_LABEL = { pending: "Pending", approved: "Approved", rejected: "Rejected", suspended: "Paused" };
const PAGE = 10;

function Pager({ page, pages, onPage, testid }) {
  if (pages <= 1) return null;
  return (
    <div className="admin-pager" data-testid={testid}>
      <button className="btn sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="pager-prev">Prev</button>
      <span className="dim">Page {page} of {pages}</span>
      <button className="btn sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="pager-next">Next</button>
    </div>
  );
}

function Publishers() {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("date"); // date | name
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    adminListCompanies(filter === "all" ? null : filter)
      .then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter, q, sort]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    let list = rows.filter((c) =>
      !t || (c.name || "").toLowerCase().includes(t) || (c.domain || "").toLowerCase().includes(t) || (c.ownerEmail || "").toLowerCase().includes(t));
    list = [...list].sort((a, b) => sort === "name"
      ? (a.name || "").localeCompare(b.name || "")
      : new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    return list;
  }, [rows, q, sort]);
  const pages = Math.ceil(filtered.length / PAGE) || 1;
  const view = filtered.slice((page - 1) * PAGE, page * PAGE);

  const act = async (fn, c, msg) => { await fn(c.id); toast.success(msg); load(); };
  const remove = async (c) => {
    if (!window.confirm(`Permanently delete ${c.name}? This removes all its users, creatives and data.`)) return;
    const r = await deleteCompany(c.id);
    toast.success(`${c.name} deleted (${r.deletedUsers} users, ${r.deletedCreatives} creatives)`);
    load();
  };

  return (
    <>
      <div className="seg" role="tablist" data-testid="admin-filter">
        {["pending", "approved", "suspended", "rejected", "all"].map((f) => (
          <button key={f} className="seg-btn" aria-selected={filter === f}
            onClick={() => setFilter(f)} data-testid={`filter-${f}`}>
            {f === "all" ? "All" : STATUS_LABEL[f]}
          </button>
        ))}
      </div>

      <div className="admin-toolbar">
        <div className="admin-search">
          <Search size={15} />
          <input placeholder="Search name, domain or owner…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pub-search" />
        </div>
        <button className="btn sm" onClick={() => setSort(sort === "date" ? "name" : "date")} data-testid="pub-sort">
          <ArrowUpDown size={14} /> Sort: {sort === "date" ? "Application date" : "Name"}
        </button>
        <span className="dim" style={{ marginLeft: "auto" }}>{filtered.length} publisher{filtered.length === 1 ? "" : "s"}</span>
      </div>

      {loading ? <div className="empty">Loading…</div>
        : filtered.length === 0 ? <div className="empty" data-testid="admin-empty">No publishers match.</div>
          : (
            <table className="admin-table" data-testid="admin-table">
              <thead><tr><th>Publisher</th><th>Owner</th><th>Users</th><th>Applied</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {view.map((c) => (
                  <tr key={c.id} data-testid={`company-row-${c.id}`}>
                    <td><b>{c.name}</b><div className="mono dim">{c.domain}</div></td>
                    <td>{c.ownerEmail || "—"}</td>
                    <td>{c.userCount}</td>
                    <td className="dim">{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : "—"}</td>
                    <td><span className={`pill pill-${c.status}`}>{STATUS_LABEL[c.status] || c.status}</span></td>
                    <td className="row-acts">
                      {(c.status === "pending" || c.status === "suspended" || c.status === "rejected") && (
                        <button className="btn primary sm" onClick={() => act(approveCompany, c, `${c.name} approved`)} data-testid={`approve-${c.id}`}>Approve</button>
                      )}
                      {c.status === "approved" && (
                        <button className="btn sm" onClick={() => act(pauseCompany, c, `${c.name} paused`)} data-testid={`pause-${c.id}`}>Pause</button>
                      )}
                      {c.status === "pending" && (
                        <button className="btn sm" onClick={() => act(rejectCompany, c, `${c.name} rejected`)} data-testid={`reject-${c.id}`}>Reject</button>
                      )}
                      {c.status === "rejected" && (
                        <button className="btn sm" onClick={() => act(reopenCompany, c, `${c.name} moved to pending`)} data-testid={`reopen-${c.id}`}>Re-open</button>
                      )}
                      <button className="btn danger sm" onClick={() => remove(c)} data-testid={`delete-${c.id}`}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      <Pager page={page} pages={pages} onPage={setPage} testid="pub-pager" />
    </>
  );
}

function AllUsers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => { adminListUsers().then(setRows).catch(() => setRows([])).finally(() => setLoading(false)); }, []);
  useEffect(() => { setPage(1); }, [q]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.filter((u) => !t || [u.email, u.name, u.companyName, u.role].some((x) => (x || "").toLowerCase().includes(t)));
  }, [rows, q]);
  const pages = Math.ceil(filtered.length / PAGE) || 1;
  const view = filtered.slice((page - 1) * PAGE, page * PAGE);

  return (
    <>
      <div className="admin-toolbar">
        <div className="admin-search">
          <Search size={15} />
          <input placeholder="Search users by email, name, company or role…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-search" />
        </div>
        <span className="dim" style={{ marginLeft: "auto" }}>{filtered.length} user{filtered.length === 1 ? "" : "s"}</span>
      </div>
      {loading ? <div className="empty">Loading…</div>
        : filtered.length === 0 ? <div className="empty" data-testid="users-empty">No users match.</div>
          : (
            <table className="admin-table" data-testid="users-table">
              <thead><tr><th>User</th><th>Company</th><th>Role</th><th>Login</th><th>Joined</th><th>Status</th></tr></thead>
              <tbody>
                {view.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td><b>{u.name || u.email.split("@")[0]}</b><div className="mono dim">{u.email}</div></td>
                    <td>{u.companyName}</td>
                    <td><span className="pill">{u.role}</span></td>
                    <td className="dim">{u.authProvider === "google" ? "Google" : "Password"}</td>
                    <td className="dim">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}</td>
                    <td><span className={`pill pill-${u.companyStatus}`}>{STATUS_LABEL[u.companyStatus] || u.companyStatus}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      <Pager page={page} pages={pages} onPage={setPage} testid="user-pager" />
    </>
  );
}

function Templates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    adminListTemplates().then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const promote = async (t) => {
    if (!window.confirm(`Make "${t.name}" a Standard template available to every account?`)) return;
    await promoteTemplate(t.id); toast.success(`${t.name} is now a Standard template`); load();
  };
  const remove = async (t) => {
    if (!window.confirm(`Permanently delete "${t.name}"? Creatives built on it will stop working.`)) return;
    await adminDeleteTemplate(t.id); toast.success(`${t.name} deleted`); load();
  };

  return (
    <>
      <p className="admin-note" data-testid="tpl-note">
        <b>Standard</b> templates are available to every account. <b>Company</b> templates were uploaded by one publisher and
        only they can see them — promote one to make it Standard for everyone.
      </p>
      {loading ? <div className="empty">Loading…</div>
        : rows.length === 0 ? <div className="empty">No templates yet.</div>
          : (
            <table className="admin-table" data-testid="tpl-table">
              <thead><tr><th>Template</th><th>Scope</th><th>Owner</th><th>Source</th><th></th></tr></thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} data-testid={`tpl-row-${t.id}`}>
                    <td><b>{t.name}</b><div className="mono dim">{t.id}</div></td>
                    <td><span className={`pill ${t.scope === "standard" ? "pill-approved" : ""}`}>{t.scope === "standard" ? "Standard" : "Company"}</span></td>
                    <td>{t.companyName}</td>
                    <td className="dim">{t.source === "disk" ? "Built-in" : "Uploaded"}</td>
                    <td className="row-acts">
                      {t.canPromote && <button className="btn primary sm" onClick={() => promote(t)} data-testid={`promote-${t.id}`}>Promote to Standard</button>}
                      {t.canDelete
                        ? <button className="btn danger sm" onClick={() => remove(t)} data-testid={`tpl-delete-${t.id}`}>Delete</button>
                        : <span className="dim" style={{ fontSize: 12 }}>{t.source === "disk" ? "ships with app" : "standard · locked"}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
    </>
  );
}

const WA_CRED_FIELDS = {
  webhook: [{ key: "webhookUrl", label: "Webhook URL (we POST {to, message})" }],
  meta: [
    { key: "phoneNumberId", label: "Phone Number ID" },
    { key: "accessToken", label: "System User Access Token", secret: true },
    { key: "templateName", label: "Approved Template Name (e.g. plain_text)" },
    { key: "templateLanguage", label: "Template Language code (e.g. en) — must match WhatsApp Manager exactly" },
    { key: "bodyVar", label: "Body variable index (set to 1 to fill the template's {{1}})" },
    { key: "otpTemplateName", label: "OTP Template Name (Authentication category — falls back to the template above if blank)" },
    { key: "otpTemplateLanguage", label: "OTP Template Language code (falls back to the language above if blank)" },
    { key: "otpHasButton", label: "OTP template has a Copy Code / One-Tap button? (true/false, default true)" },
  ],
  twilio: [
    { key: "accountSid", label: "Account SID" },
    { key: "authToken", label: "Auth Token", secret: true },
    { key: "fromNumber", label: "WhatsApp From Number" },
    { key: "contentSid", label: "Content Template SID (HX…)" },
  ],
  msg91: [{ key: "authKey", label: "Auth Key", secret: true }, { key: "templateName", label: "Template Name" }],
  gupshup: [{ key: "apiKey", label: "API Key", secret: true }, { key: "appName", label: "App Name" }],
};
const WA_NOT_WIRED = { twilio: 1, msg91: 1, gupshup: 1 };

function Messaging() {
  const [provider, setProvider] = useState("mock");
  const [creds, setCreds] = useState({});
  const [savedKeys, setSavedKeys] = useState([]);
  const [usage, setUsage] = useState([]);
  const [otpStats, setOtpStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminGetWhatsapp()
      .then((d) => {
        setProvider(d.provider || "mock"); setSavedKeys(d.credentialKeys || []); setUsage(d.usage || []);
        setOtpStats(d.otpStats || null);
      })
      .catch(() => toast.error("Could not load WhatsApp settings"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    await adminSaveWhatsapp({ provider, credentials: creds });
    toast.success("WhatsApp settings saved"); setCreds({}); load();
  };

  if (loading) return <div className="empty">Loading…</div>;
  const fields = WA_CRED_FIELDS[provider] || [];

  return (
    <>
      <div className="dim" style={{ margin: "4px 0 14px", maxWidth: 720 }}>
        WhatsApp delivery for “Send to WhatsApp” CTA templates. <b>Mock mode</b> logs messages without sending —
        pick a provider and save credentials when you onboard one. Usage below powers per-message billing.
      </div>

      <div className="admin-toolbar" style={{ gap: 10 }}>
        <label className="dim" htmlFor="wa-provider">Provider</label>
        <select id="wa-provider" className="admin-table-select" value={provider}
          onChange={(e) => setProvider(e.target.value)} data-testid="wa-provider-select">
          <option value="mock">Mock / log only (dev)</option>
          <option value="webhook">Custom webhook</option>
          <option value="meta">Meta WhatsApp Cloud API</option>
          <option value="twilio">Twilio WhatsApp</option>
          <option value="msg91">MSG91</option>
          <option value="gupshup">Gupshup</option>
        </select>
        <button className="btn primary sm" onClick={save} data-testid="wa-save-btn">Save</button>
        {WA_NOT_WIRED[provider] && (
          <span className="dim" data-testid="wa-not-wired" style={{ fontSize: 12 }}>
            Adapter not wired yet — credentials are stored, dispatch still runs in mock until integration.
          </span>
        )}
      </div>

      {fields.length > 0 && (
        <div style={{ maxWidth: 520, margin: "10px 0 22px", display: "grid", gap: 10 }} data-testid="wa-cred-form">
          {fields.map((f) => (
            <label key={f.key} style={{ display: "grid", gap: 4 }}>
              <span className="dim" style={{ fontSize: 12 }}>
                {f.label}{savedKeys.includes(f.key) ? " · saved" : ""}
              </span>
              <input type={f.secret ? "password" : "text"} value={creds[f.key] || ""}
                placeholder={savedKeys.includes(f.key) ? "•••••• (leave blank to keep)" : ""}
                onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                data-testid={`wa-cred-${f.key}`} />
            </label>
          ))}
        </div>
      )}

      {otpStats && (
        <div className="dim" style={{ margin: "4px 0 10px" }} data-testid="wa-otp-stats">
          OTP codes sent: <b>{otpStats.sent}</b> &nbsp;·&nbsp; Verified: <b>{otpStats.verified}</b>
        </div>
      )}

      <div className="eyebrow" style={{ margin: "18px 0 8px" }}>Message usage by company</div>
      {usage.length === 0 ? <div className="empty" data-testid="wa-usage-empty">No WhatsApp messages sent yet.</div> : (
        <table className="admin-table" data-testid="wa-usage-table">
          <thead><tr><th>Company</th><th>Messages sent</th><th>Last sent</th></tr></thead>
          <tbody>
            {usage.map((u) => (
              <tr key={u.companyId || "none"} data-testid={`wa-usage-${u.companyId}`}>
                <td><b>{u.companyName}</b></td>
                <td>{u.count}</td>
                <td className="dim">{u.lastSent ? new Date(u.lastSent).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export default function AdminPanel() {
  const [tab, setTab] = useState("publishers");
  const admin = getUser();
  const TABS = [
    ["publishers", "Publishers"],
    ["users", "All Users"],
    ["templates", "Templates"],
    ["messaging", "Messaging"],
    ["docs", "Docs"],
  ];

  return (
    <div className="app">
      <header className="bar">
        <h1 className="page-title" data-testid="page-title">Admin Console</h1>
        <div className="spacer" />
        <span className="who" data-testid="admin-who">{admin?.email}</span>
      </header>

      <div className="admin-wrap">
        <div className="admin-tabs" data-testid="admin-tabs">
          {TABS.map(([id, label]) => (
            <button key={id} className={`admin-tab ${tab === id ? "active" : ""}`}
              onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}>{label}</button>
          ))}
        </div>

        {tab === "publishers" && <Publishers />}
        {tab === "users" && <AllUsers />}
        {tab === "templates" && <Templates />}
        {tab === "messaging" && <Messaging />}
        {tab === "docs" && <DocsViewer />}
      </div>
    </div>
  );
}
